import { bundle } from '@ms-cloudpack/bundler';
import type { BundleOptions } from '@ms-cloudpack/common-types';
import { writeJson } from '@ms-cloudpack/json-utilities';
import { getExportsMap } from '@ms-cloudpack/package-utilities';
import { SpanStatusCode } from '@ms-cloudpack/telemetry';
import { formatPackageName } from '@ms-cloudpack/task-reporter';
import path from 'path';
import { prepareOutputPath } from '../common/prepareOutputPath.js';
import type { BundleRequest } from '../types/BundleRequest.js';
import { getBundleInfo } from './getBundleInfo.js';
import { getBundleLocation } from './getBundleLocation.js';
import { isCachedResultValid } from './isCachedResultValid.js';
import { readResultFromCache } from './readResultFromCache.js';
import { saveResultToCache } from './saveResultToCache.js';
import { formatBundleTaskResult } from './formatBundleTaskResult.js';
import type { PartialContext } from '../types/Context.js';
import { writeStubForDensityEntries } from './writeStubForDensityEntries.js';
import { getExternalsFromPackage } from './getExternalsFromPackage.js';
import { getEntriesMapFromPackage } from './getEntriesMapFromPackage.js';

/**
 * Options for a bundle task.
 */
export interface BundleTaskOptions {
  /** Force a bundle to be rerun */
  force?: boolean;
  /** Hash to avoid re-computing */
  hash?: string;
  /** Source hash to avoid recomputing */
  sourceHash?: string;
  /** Retry count for the task */
  retryCount?: number;
  bundleRequest: BundleRequest;
}

export type BundleTaskContext = PartialContext<
  'packages' | 'packageHashes' | 'reporter' | 'telemetryClient' | 'remoteCacheClient',
  'resolveMap' | 'config' | 'sequence' | 'cachePath' | 'dedupeAliases'
>;

/**
 * Create and run a TaskReporter task which calls `bundle()` from `@ms-cloudpack/bundler` and formats the result.
 */
export async function bundleTask(options: BundleTaskOptions, context: BundleTaskContext): Promise<void> {
  const { bundleRequest, retryCount } = options;
  const { packageName, version } = bundleRequest;
  const { reporter, telemetryClient, session } = context;
  const { sequence } = session;

  const task = reporter.addTask(
    `Bundle ${formatPackageName({ name: packageName, version })}${retryCount ? ` (retry ${retryCount})` : ''}`,
  );

  try {
    await telemetryClient.tracer.startActiveSpan('BUNDLE_TASK', async (span) => {
      await bundleTaskInternal(options, context);

      span.setAttributes({
        sequence,
        isCacheHit: !!bundleRequest.resultFromCache,
        isExternal: bundleRequest.isExternal,
        packageName: bundleRequest.packageName,
        packageVersion: bundleRequest.version,
        bundleSource: bundleRequest.result?.bundleSource,
        bundler: bundleRequest.result?.bundler,
        bundlerVersion: bundleRequest.result?.bundlerVersion,
        ...(retryCount !== undefined && { retryCount }),
      });

      if (bundleRequest.result?.errors?.length) {
        // Set the status to `ERROR`
        span.setStatus({ code: SpanStatusCode.ERROR });
      }
    });
  } catch (err) {
    // If an unexpected error is thrown, add it to the result.
    // This ensures consistent handling of either actual bundler errors or exceptions.
    bundleRequest.result ??= {};
    bundleRequest.result.errors ??= [];
    bundleRequest.result.errors.push({
      text: `Unexpected exception while bundling: ${(err as Error).stack || String(err)}`,
      source: 'bundleTask',
    });
  }

  task.complete(formatBundleTaskResult(bundleRequest, reporter));
}

/**
 * Actually read the result from the cache or call `bundle()`.
 * NOTE: The result is returned by mutating `options.bundleRequest.result`.
 */
async function bundleTaskInternal(options: BundleTaskOptions, context: BundleTaskContext): Promise<void> {
  const { bundleRequest, force, hash, sourceHash } = options;
  const { packageName, version, targetEnvironment } = bundleRequest;
  const { packageHashes, packages, session } = context;
  const { config, dedupeAliases } = session;
  const { mode } = config;

  // If force is set, dispose the existing result.
  if (force && bundleRequest.result?.dispose) {
    bundleRequest.result.dispose();
    bundleRequest.result = undefined;
  }

  const { packagePath, isExternal, isIncremental, disableSourceMaps, shouldGetBundleInfo } = bundleRequest;

  const outputPath =
    bundleRequest.outputPath ??
    (await getBundleLocation({ packagePath, targetEnvironment, shouldRecalculate: force, hash }, context)).path;

  // Rehydrate the result if not incremental.
  if (bundleRequest.result === undefined && !force) {
    bundleRequest.result = await readResultFromCache(
      { packageName, version, inputPath: packagePath, outputPath, targetEnvironment },
      context,
    );
  }

  const cacheValidationHash = !isExternal
    ? sourceHash ||
      (await packageHashes.get({
        packagePath,
        targetEnvironment,
        isSourceHashingEnabled: true,
        shouldRecalculate: true,
      }))
    : undefined;

  // if the cached result is valid, use it.
  if (!force && isCachedResultValid({ bundleRequest, hash: cacheValidationHash })) {
    console.debug(`Using cached result for ${bundleRequest.packageName}@${bundleRequest.version}`);
    bundleRequest.resultFromCache = true;
  } else {
    bundleRequest.resultFromCache = false;

    // Only use cached result if it's successful.
    await prepareOutputPath(outputPath);

    if (bundleRequest.result?.rebuild) {
      bundleRequest.result = {
        ...(await bundleRequest.result.rebuild()),
        outputPath: bundleRequest.result.outputPath,
        hash: cacheValidationHash,
      };
    } else {
      const {
        bundler,
        bundlerOptions,
        inlinedDependencies,
        dynamicImports,
        bundlerCapabilities,
        ignoredBundlerWarnings,
        unsafeCjsExportNames,
      } = (await packages.get(packagePath)).cloudpack || {};

      const bundleOptions: BundleOptions = {
        // We need the package name without transforms to ensure we get the original name.
        packageName: (await packages.get(packagePath, { disableTransforms: true })).name,
        bundler,
        minify: mode === 'production',
        targetEnvironment,
        bundlerOptions,
        inputPath: packagePath,
        outputPath,
        incremental: isIncremental !== undefined ? isIncremental : false,
        sourcemap: !disableSourceMaps,
        entries: await getEntriesMapFromPackage({ inputPath: packagePath, targetEnvironment }, { packages, config }),
        inlined: inlinedDependencies,
        external: await getExternalsFromPackage({ inputPath: packagePath }, { packages }),
        dynamicImports,
        ignoredBundlerWarnings,
        bundlerCapabilities,
        unsafeCjsExportNames,
        dedupeAliases,
        hmr: !isExternal && !!config.features?.hmr,
      };

      const bundleInternalResult = await bundle(bundleOptions, { config });
      bundleRequest.result = {
        ...bundleInternalResult,
        outputPath: bundleOptions.outputPath ?? outputPath,
        hash: cacheValidationHash,
        bundleSource: 'bundler',
      };

      if (bundlerCapabilities?.density) {
        // Get export maps that need to be written as stubs for density.
        // If there are density exports, write stubs for them.
        const densityResult = await writeStubForDensityEntries({
          options: {
            packageName,
            packagePath,
            result: bundleInternalResult,
            densityOptions: bundlerCapabilities.density,
          },
          context: { packages, config },
        });

        // Add errors and warnings to the appropriate arrays
        if (densityResult.errors.length > 0) {
          bundleRequest.result.errors = [...(bundleRequest.result.errors ?? []), ...densityResult.errors];
        }

        if (densityResult.warnings.length > 0) {
          bundleRequest.result.warnings = [...(bundleRequest.result.warnings ?? []), ...densityResult.warnings];
        }
      }
    }

    // Write the result to disk.
    await saveResultToCache(bundleRequest.result);
  }

  // If we should find required imports and exports per bundle
  if (shouldGetBundleInfo && !bundleRequest.result?.errors?.length && bundleRequest.result?.outputFiles?.length) {
    // Exports map is needed to find the bundle info per entry we intend to bundle.
    // Exports map is used instead of bundleRequest.entries since it is easier to use
    // because it contains the paths other packages use to import the entry and
    // the relative location of the bundled files.
    const exportsMap = await getExportsMap({ packagePath }, { packages, config });

    const bundleInfo = await getBundleInfo({
      outputPath,
      outputFiles: bundleRequest.result.outputFiles,
      exportsMap,
      isExternal,
      packagePath,
    });

    bundleRequest.result.info = bundleInfo;

    if (config.logBundleInfo) {
      // Write to cache for future use. (Ignore the promise result.)
      void writeJson(path.join(outputPath, 'bundle-info.json'), bundleInfo);
    }
  }
}
